import React from 'react';
import { useHistory } from 'react-router-dom';
import '../crm.css';
import { crmBasePath, isCrmPortalMode } from '../utils/crmEnv';
import PropertyMatchesModal from './PropertyMatchesModal';
import { createPropHuntAd, syncPropHuntStatus, uploadFile, getProperty, updateProperty } from '../utils/crmApi';
import { useToast } from './Toast';

const statusLabels = {
    available: 'Available',
    hold: 'Hold',
    booked: 'Booked',
    sold: 'Sold',
    inactive: 'Inactive',
};

const typeLabels = {
    plot: 'Plot',
    flat: 'Flat',
    villa: 'Villa',
    commercial: 'Commercial',
    farm: 'Farm',
    other: 'Other',
};

const formatCurrency = (value) => {
    if (value === undefined || value === null || value === '') return '??';
    return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        maximumFractionDigits: 0,
    }).format(Number(value) || 0);
};

const formatArea = (area, unit) => {
    if (area === undefined || area === null || area === '') return '?';
    return `${area} ${unit || 'sqft'}`;
};

const toNumberOrNull = (value) => {
    if (value === undefined || value === null || value === '') return null;
    const num = Number(value);
    return Number.isFinite(num) ? num : null;
};

const compactObject = (value) => Object.fromEntries(
    Object.entries(value || {}).filter(([, current]) => {
        if (current === undefined || current === null) return false;
        if (typeof current === 'string' && current.trim() === '') return false;
        return true;
    })
);

const parseImageUrls = (value) => String(value || '')
    .split('\n')
    .map((url) => url.trim())
    .filter(Boolean);

const normalizePublicDraftForm = ({ form, advertiseOnProphunt, imageUrls }) => ({
    ...form,
    expectedRent: form?.expectedRent ?? '',
    securityDeposit: form?.securityDeposit ?? '',
    amountNegotiable: Boolean(form?.amountNegotiable),
    advertiseOnProphunt: Boolean(advertiseOnProphunt),
    imageUrls,
});

const normalizeProphuntStatus = (value) => String(value || '').trim().toLowerCase();

const isInactiveProphuntStatus = (status) => (
    [
        'delete',
        'deleted',
        'remove',
        'removed',
        'inactive',
        'archive',
        'archived',
        'expire',
        'expired',
        'closed',
        'deactivate',
        'unpublish',
        'not_found',
        'failed',
        'error',
        'rejected',
    ].some((needle) => status.includes(needle))
);

const hasActiveProphuntAd = (meta) => {
    if (!meta || typeof meta !== 'object') return false;
    if (typeof meta.isActive === 'boolean') return meta.isActive;

    const status = normalizeProphuntStatus(meta.providerStatus || meta.status);
    if (status && isInactiveProphuntStatus(status)) return false;
    if (status && ['submitted', 'success', 'active', 'live', 'published', 'updated'].some((needle) => status.includes(needle))) {
        return true;
    }
    if (meta.adId || meta.adUrl) return true;
    return Boolean(meta.externalRefId);
};

function PropertyCard({ property, onEdit, onDelete }) {
    const history = useHistory();
    const { showSuccess, showError } = useToast();
    const basePath = crmBasePath();
    const isPortal = isCrmPortalMode();
    const hasProphuntAd = React.useMemo(() => {
        return hasActiveProphuntAd(property?.prophunt);
    }, [property?.prophunt]);

    const handleClick = () => {
        history.push(`${basePath}/properties/${property.id}`);
    };

    const handleEdit = (e) => {
        e.stopPropagation();
        onEdit?.(property);
    };

    const handleDelete = (e) => {
        e.stopPropagation();
        onDelete?.(property.id);
    };

    const [showMatches, setShowMatches] = React.useState(false);
    const [showPublicModal, setShowPublicModal] = React.useState(false);
    const [advertiseOnProphunt, setAdvertiseOnProphunt] = React.useState(true);
    const [isPushing, setIsPushing] = React.useState(false);
    const [adCreated, setAdCreated] = React.useState(hasProphuntAd);
    const [publicImages, setPublicImages] = React.useState('');
    const [publicImageFiles, setPublicImageFiles] = React.useState([]);
    const [missingFields, setMissingFields] = React.useState([]);
    const [publicForm, setPublicForm] = React.useState({
        title: '',
        projectName: '',
        propertyType: '',
        listingType: '',
        city: '',
        locality: '',
        pincode: '',
        description: '',
        expectedRent: '',
        securityDeposit: '',
        amountNegotiable: false,
    });
    const [publicSource, setPublicSource] = React.useState(property);
    const [isSavingDraft, setIsSavingDraft] = React.useState(false);
    const syncKeyRef = React.useRef('');
    const draftAutosaveTimerRef = React.useRef(null);
    const lastSavedDraftSnapshotRef = React.useRef('');
    const buildDraftSnapshot = React.useCallback((formValue, imageText, advertiseFlag) => {
        const normalized = normalizePublicDraftForm({
            form: formValue,
            advertiseOnProphunt: advertiseFlag,
            imageUrls: parseImageUrls(imageText),
        });
        return JSON.stringify(normalized);
    }, []);

    React.useEffect(() => {
        setAdCreated(hasProphuntAd);
    }, [hasProphuntAd]);

    React.useEffect(() => {
        if (isPortal) return;
        const meta = property?.prophunt;
        const canSyncByReference = Boolean(meta && typeof meta === 'object' && meta.externalRefId);
        if (!hasActiveProphuntAd(meta) && !canSyncByReference) return;

        const syncKey = [
            property?.id || '',
            meta?.externalRefId || '',
            meta?.adId || '',
            meta?.adUrl || '',
            meta?.status || '',
            meta?.updatedAt || '',
        ].join('|');
        if (!syncKey || syncKeyRef.current === syncKey) return;
        syncKeyRef.current = syncKey;

        let isActive = true;
        const run = async () => {
            try {
                const result = await syncPropHuntStatus({
                    propertyId: property.id,
                    externalRefId: meta?.externalRefId,
                    adId: meta?.adId,
                    adType: meta?.adType,
                    adUrl: meta?.adUrl,
                });
                if (!isActive) return;
                const nextMeta = result?.prophunt || meta;
                setAdCreated(hasActiveProphuntAd(nextMeta));
                setPublicSource((prev) => ({ ...(prev || property), prophunt: nextMeta }));
            } catch {
                // Ignore sync failures and keep current local state.
            }
        };
        run();
        return () => {
            isActive = false;
        };
    }, [isPortal, property?.id, property?.prophunt]);

    const locationLabel = [property.locality, property.city].filter(Boolean).join(', ') || property.city || 'Location not set';
    const status = property.status || 'available';
    const propertyType = property.propertyType || 'other';
    const descriptionValue = property.description || [property.title, locationLabel].filter(Boolean).join(' - ') || 'Property listing';

    React.useEffect(() => {
        let isActive = true;
        const hydrate = async () => {
            if (!showPublicModal) return;
            let latest = property;
            try {
                const fresh = await getProperty(property.id);
                if (fresh) {
                    latest = fresh;
                }
            } catch {
                // ignore and use current property snapshot
            }
            if (!isActive) return;
            setPublicSource(latest);
            setAdCreated(hasActiveProphuntAd(latest?.prophunt));
            const existingForm = (latest?.prophunt && typeof latest.prophunt === 'object' && latest.prophunt.formData && typeof latest.prophunt.formData === 'object')
                ? latest.prophunt.formData
                : {};
            const savedImageUrls = Array.isArray(existingForm.imageUrls)
                ? existingForm.imageUrls
                : [];
            const urls = savedImageUrls.length > 0
                ? savedImageUrls.join('\n')
                : (Array.isArray(latest.imageUrls) ? latest.imageUrls.join('\n') : '');
            const nextAdvertise = typeof existingForm.advertiseOnProphunt === 'boolean'
                ? existingForm.advertiseOnProphunt
                : (typeof latest?.prophunt?.advertiseOnProphunt === 'boolean' ? latest.prophunt.advertiseOnProphunt : true);
            setPublicImages(urls);
            setAdvertiseOnProphunt(nextAdvertise);
            setMissingFields([]);
            setPublicImageFiles([]);
            const listingRaw = String(latest.listingType || latest.listingIntent || '').toLowerCase();
            const listingValue = listingRaw.includes('rent')
                ? 'rent'
                : (listingRaw.includes('sell') || listingRaw.includes('sale'))
                    ? 'sell'
                    : '';
            const nextForm = {
                title: existingForm.title || latest.title || '',
                projectName: existingForm.projectName || latest.projectName || '',
                propertyType: existingForm.propertyType || latest.propertyType || '',
                listingType: existingForm.listingType || listingValue,
                city: existingForm.city || latest.city || '',
                locality: existingForm.locality || latest.locality || '',
                pincode: existingForm.pincode || latest.pincode || '',
                description: existingForm.description || latest.description || [latest.title, locationLabel].filter(Boolean).join(' - ') || '',
                expectedRent: existingForm.expectedRent ?? latest.price ?? '',
                securityDeposit: existingForm.securityDeposit ?? latest.securityDeposit ?? '',
                amountNegotiable: typeof existingForm.amountNegotiable === 'boolean'
                    ? existingForm.amountNegotiable
                    : Boolean(latest.negotiable),
            };
            setPublicForm(nextForm);
            lastSavedDraftSnapshotRef.current = buildDraftSnapshot(nextForm, urls, nextAdvertise);
        };

        hydrate();
        return () => {
            isActive = false;
        };
    }, [showPublicModal, property.id, locationLabel, buildDraftSnapshot]);

    const handlePublicFilesChange = (e) => {
        const files = Array.from(e.target.files || []);
        setPublicImageFiles(files);
    };

    const readFileAsBase64 = (file) => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            const result = reader.result;
            if (typeof result === 'string') {
                const base64 = result.split(',')[1] || '';
                resolve(base64);
            } else {
                resolve('');
            }
        };
        reader.onerror = () => reject(new Error('Failed to read file'));
        reader.readAsDataURL(file);
    });

    const resolveAdType = () => {
        const raw = String(
            publicForm.listingType
            || publicSource?.listingType
            || publicSource?.listingIntent
            || property.listingType
            || property.listingIntent
            || ''
        ).toLowerCase();
        if (raw.includes('rent')) return 'listforrent';
        if (raw.includes('sale') || raw.includes('sell')) return 'listforsale';
        return 'listforsale';
    };

    const persistPublicDraft = React.useCallback(async ({ force = false, toastOnError = false } = {}) => {
        if (!property?.id) return false;
        const draftForm = normalizePublicDraftForm({
            form: publicForm,
            advertiseOnProphunt,
            imageUrls: parseImageUrls(publicImages),
        });
        const snapshot = JSON.stringify(draftForm);
        if (!force && snapshot === lastSavedDraftSnapshotRef.current) {
            return true;
        }

        try {
            setIsSavingDraft(true);
            const currentMeta = (publicSource?.prophunt && typeof publicSource.prophunt === 'object')
                ? publicSource.prophunt
                : ((property?.prophunt && typeof property.prophunt === 'object') ? property.prophunt : {});
            const nextMeta = {
                ...currentMeta,
                formData: draftForm,
                draftSavedAt: new Date().toISOString(),
            };
            const updated = await updateProperty(property.id, { prophunt: nextMeta });
            lastSavedDraftSnapshotRef.current = snapshot;
            if (updated) {
                setPublicSource(updated);
                setAdCreated(hasActiveProphuntAd(updated?.prophunt));
            } else {
                setPublicSource((prev) => ({ ...(prev || property), prophunt: nextMeta }));
            }
            return true;
        } catch (error) {
            console.error('Failed to save PropHunt form draft', error);
            if (toastOnError) {
                showError('Failed to save draft changes');
            }
            return false;
        } finally {
            setIsSavingDraft(false);
        }
    }, [property?.id, property?.prophunt, property, publicForm, advertiseOnProphunt, publicImages, publicSource?.prophunt, showError]);

    const handleClosePublicModal = React.useCallback(async () => {
        if (draftAutosaveTimerRef.current) {
            clearTimeout(draftAutosaveTimerRef.current);
            draftAutosaveTimerRef.current = null;
        }
        const saved = await persistPublicDraft({ force: false, toastOnError: true });
        if (saved) {
            setShowPublicModal(false);
        }
    }, [persistPublicDraft]);

    React.useEffect(() => {
        if (!showPublicModal) return undefined;
        const snapshot = buildDraftSnapshot(publicForm, publicImages, advertiseOnProphunt);
        if (snapshot === lastSavedDraftSnapshotRef.current) {
            return undefined;
        }
        if (draftAutosaveTimerRef.current) {
            clearTimeout(draftAutosaveTimerRef.current);
        }
        draftAutosaveTimerRef.current = setTimeout(() => {
            persistPublicDraft();
        }, 700);
        return () => {
            if (draftAutosaveTimerRef.current) {
                clearTimeout(draftAutosaveTimerRef.current);
                draftAutosaveTimerRef.current = null;
            }
        };
    }, [showPublicModal, publicForm, publicImages, advertiseOnProphunt, buildDraftSnapshot, persistPublicDraft]);

    const handlePushToProphunt = async () => {
        try {
            setIsPushing(true);
            const draftSaved = await persistPublicDraft({ force: true, toastOnError: false });
            if (!draftSaved) {
                showError('Failed to save form draft. Please try again.');
                setIsPushing(false);
                return;
            }
            const isDebug = typeof window !== 'undefined' && window.localStorage?.getItem('PROPHUNT_DEBUG') === '1';
            const adType = resolveAdType();
            const description = publicForm.description || descriptionValue;
            const expectedRentValue = toNumberOrNull(publicForm.expectedRent);
            const securityDepositValue = toNumberOrNull(publicForm.securityDeposit);
            const sourcePrice = toNumberOrNull(publicSource?.price) ?? toNumberOrNull(property.price);
            const listingTypeValue = (publicForm.listingType || '').toLowerCase();
            const locationParts = [
                publicForm.locality || property.locality,
                publicForm.city || property.city,
                publicForm.pincode || property.pincode,
            ].filter(Boolean);
            const descriptionWithLocation = locationParts.length > 0
                ? `${description} | Location: ${locationParts.join(', ')}`
                : description;
            const imageUrls = publicImages
                .split('\n')
                .map((url) => url.trim())
                .filter(Boolean);
            let uploadedUrls = [];

            if (publicImageFiles.length > 0) {
                const uploaded = await Promise.all(
                    publicImageFiles.map(async (file) => {
                        const base64Data = await readFileAsBase64(file);
                        return uploadFile({
                            entityType: 'property',
                            entityId: property.id,
                            fileName: file.name,
                            contentType: file.type || 'application/octet-stream',
                            base64Data,
                        });
                    })
                );
                uploadedUrls = uploaded
                    .map((meta) => meta.publicUrl || meta.url || meta.b2Path)
                    .filter(Boolean);
            }

            const combinedImageUrls = [...imageUrls, ...uploadedUrls].filter(Boolean);

            const missing = [];
            if (!publicForm.title.trim()) missing.push('Title');
            if (!publicForm.propertyType) missing.push('Type');
            if (!publicForm.listingType) missing.push('Listing');
            if (!publicForm.city.trim()) missing.push('City');
            if (!publicForm.locality.trim()) missing.push('Locality');
            if (!publicForm.pincode.trim()) missing.push('Pincode');
            if (!description) missing.push('Description');
            if (listingTypeValue === 'rent' && (expectedRentValue === null || expectedRentValue < 5000)) {
                missing.push('Expected Rent (>= 5000)');
            }
            if (listingTypeValue === 'rent' && securityDepositValue === null) {
                missing.push('Security Deposit');
            }
            if (combinedImageUrls.length === 0) {
                // Images are recommended, not mandatory.
            }

            if (missing.length > 0) {
                setMissingFields(missing);
                showError(`Missing mandatory fields: ${missing.join(', ')}`);
                setIsPushing(false);
                return;
            }
            setMissingFields([]);

            const optional = compactObject({
                imageUrls: combinedImageUrls,
                bedrooms: publicSource?.bedrooms || publicSource?.bhk || property.bedrooms || property.bhk || undefined,
                bathrooms: publicSource?.bathrooms || property.bathrooms || undefined,
                carpetArea: publicSource?.carpetArea || publicSource?.area || property.carpetArea || property.area || undefined,
                area: publicSource?.area || property.area || undefined,
                areaUnit: publicSource?.areaUnit || property.areaUnit || undefined,
                currency: publicSource?.currency || property.currency || 'INR',
                price: expectedRentValue ?? sourcePrice ?? undefined,
                expectedRent: expectedRentValue ?? undefined,
                securityDeposit: securityDepositValue ?? undefined,
                amountNegotiable: Boolean(publicForm.amountNegotiable),
                negotiable: Boolean(publicForm.amountNegotiable),
                projectName: publicForm.projectName?.trim() || undefined,
                propertyType: publicForm.propertyType || undefined,
                listingType: publicForm.listingType || undefined,
                city: publicForm.city?.trim() || undefined,
                locality: publicForm.locality?.trim() || undefined,
                pincode: publicForm.pincode?.trim() || undefined,
                title: publicForm.title?.trim() || undefined,
            });

            const payload = {
                propertyId: property.id,
                adType,
                description: descriptionWithLocation,
                advertiseOnProphunt,
                formData: {
                    ...publicForm,
                    expectedRent: expectedRentValue ?? '',
                    securityDeposit: securityDepositValue ?? '',
                    amountNegotiable: Boolean(publicForm.amountNegotiable),
                },
                optional,
            };
            if (!adType || !description) {
                throw new Error('PropHunt validation failed: missing adType or description');
            }
            if (isDebug) {
                console.info('PropHunt createAd request', {
                    url: '/api/prophunt/createAd',
                    payload,
                });
            }
            const result = await createPropHuntAd(payload);
            if (isDebug || result?.adUrl) {
                console.info('PropHunt createAd response', {
                    status: result?.status,
                    externalRefId: result?.externalRefId,
                    adId: result?.adId,
                    adType: result?.adType,
                    adUrl: result?.adUrl,
                });
            }
            showSuccess('Sent to PropHunt');
            setAdCreated(true);
            setShowPublicModal(false);
        } catch (error) {
            console.error('PropHunt push failed:', error);
            showError(error?.message || 'Failed to push to PropHunt');
        } finally {
            setIsPushing(false);
        }
    };

    return (
        <>
            <div className="crm-card" onClick={handleClick} style={{ cursor: 'pointer' }}>
                <div className="crm-card-body">
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', marginBottom: '12px' }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: 'var(--crm-gray-900)' }}>
                                {property.title || 'Untitled Property'}
                            </h3>
                            <p style={{ margin: '6px 0 0', fontSize: '13px', color: 'var(--crm-gray-500)' }}>
                                {locationLabel}
                            </p>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '6px' }}>
                            <span className={`crm-badge crm-badge-${status}`}>{statusLabels[status] || 'Status'}</span>
                            <span className="crm-badge" style={{ background: 'var(--crm-gray-100)', color: 'var(--crm-gray-700)' }}>
                                {typeLabels[propertyType] || 'Other'}
                            </span>
                        </div>
                    </div>

                    <div style={{ display: 'flex', gap: '16px', fontSize: '13px', color: 'var(--crm-gray-600)' }}>
                        <div>
                            <div style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '1px', color: 'var(--crm-gray-400)' }}>Price</div>
                            <div style={{ fontWeight: '600', color: 'var(--crm-gray-900)' }}>{formatCurrency(property.price)}</div>
                        </div>
                        <div>
                            <div style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '1px', color: 'var(--crm-gray-400)' }}>Area</div>
                            <div style={{ fontWeight: '600', color: 'var(--crm-gray-900)' }}>{formatArea(property.area, property.areaUnit)}</div>
                        </div>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', paddingTop: '12px', borderTop: '1px solid var(--crm-gray-100)', marginTop: '12px' }}>
                        <button className="crm-btn crm-btn-secondary crm-btn-sm" onClick={(e) => { e.stopPropagation(); setShowMatches(true); }}>
                            Matches
                        </button>
                        <button
                            className="crm-btn crm-btn-secondary crm-btn-sm"
                            onClick={(e) => { e.stopPropagation(); if (!adCreated) setShowPublicModal(true); }}
                            disabled={adCreated}
                        >
                            {adCreated ? 'Ad Created' : 'Make Public'}
                        </button>
                        <button className="crm-btn crm-btn-secondary crm-btn-sm" onClick={handleEdit}>
                            Edit
                        </button>
                        <button className="crm-btn crm-btn-danger crm-btn-sm" onClick={handleDelete}>
                            Delete
                        </button>
                    </div>
                </div>
            </div>
            {showMatches && (
                <div onClick={e => e.stopPropagation()} style={{ cursor: 'default' }}>
                    <React.Suspense fallback={null}>
                        <PropertyMatchesModal
                            property={property}
                            onClose={() => setShowMatches(false)}
                            onMakePublic={() => {
                                setShowMatches(false);
                                setShowPublicModal(true);
                            }}
                        />
                    </React.Suspense>
                </div>
            )}
            {showPublicModal && (
                <div className="crm-modal-overlay" onClick={() => { handleClosePublicModal(); }}>
                    <div
                        className="crm-modal"
                        onClick={(e) => e.stopPropagation()}
                        style={{ background: 'var(--crm-bg)' }}
                    >
                        <div className="crm-modal-header" style={{ background: 'var(--crm-bg)', borderBottom: '1px solid var(--crm-gray-800, #2a2a2a)' }}>
                            <h2 className="crm-modal-title">Make Public</h2>
                            <button className="crm-modal-close" onClick={handleClosePublicModal} disabled={isPushing || isSavingDraft}>x</button>
                        </div>
                        <div className="crm-modal-body">
                            <label style={{ display: 'flex', alignItems: 'center', gap: '10px', color: 'var(--crm-gray-900)' }}>
                                <input
                                    type="checkbox"
                                    checked={advertiseOnProphunt}
                                    onChange={(e) => setAdvertiseOnProphunt(e.target.checked)}
                                />
                                Advertise on Prophunt for free
                            </label>
                            <div style={{ marginTop: '16px' }}>
                                <div className="crm-form-group">
                                    <label className="crm-label required">Title</label>
                                    <input
                                        type="text"
                                        className="crm-input"
                                        value={publicForm.title}
                                        onChange={(e) => setPublicForm((prev) => ({ ...prev, title: e.target.value }))}
                                    />
                                </div>
                                <div className="crm-form-group">
                                    <label className="crm-label">Project Name</label>
                                    <input
                                        type="text"
                                        className="crm-input"
                                        value={publicForm.projectName}
                                        onChange={(e) => setPublicForm((prev) => ({ ...prev, projectName: e.target.value }))}
                                        placeholder="Eg: RMZ Residential"
                                    />
                                </div>
                                <div className="crm-form-row">
                                    <div className="crm-form-group">
                                        <label className="crm-label required">Type</label>
                                        <select
                                            className="crm-select"
                                            value={publicForm.propertyType}
                                            onChange={(e) => setPublicForm((prev) => ({ ...prev, propertyType: e.target.value }))}
                                        >
                                            <option value="">Select type</option>
                                            <option value="plot">Plot</option>
                                            <option value="flat">Flat</option>
                                            <option value="villa">Villa</option>
                                            <option value="commercial">Commercial</option>
                                            <option value="farm">Farm</option>
                                            <option value="other">Other</option>
                                        </select>
                                    </div>
                                    <div className="crm-form-group">
                                        <label className="crm-label required">Listing</label>
                                        <select
                                            className="crm-select"
                                            value={publicForm.listingType}
                                            onChange={(e) => setPublicForm((prev) => ({ ...prev, listingType: e.target.value }))}
                                        >
                                            <option value="">Select listing</option>
                                            <option value="sell">For Sale</option>
                                            <option value="rent">For Rent</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="crm-form-group">
                                    <label className={`crm-label ${publicForm.listingType === 'rent' ? 'required' : ''}`}>
                                        {publicForm.listingType === 'rent' ? 'Expected Rent' : 'Expected Price'}
                                    </label>
                                    <input
                                        type="range"
                                        min={0}
                                        max={2000000}
                                        step={1000}
                                        value={Number(publicForm.expectedRent || 0)}
                                        onChange={(e) => setPublicForm((prev) => ({ ...prev, expectedRent: e.target.value }))}
                                        style={{ width: '100%', marginBottom: '10px' }}
                                    />
                                    <input
                                        type="number"
                                        className="crm-input"
                                        value={publicForm.expectedRent}
                                        onChange={(e) => setPublicForm((prev) => ({ ...prev, expectedRent: e.target.value }))}
                                        min={0}
                                        step={1000}
                                        placeholder={publicForm.listingType === 'rent' ? 'Expected rent in INR' : 'Expected price in INR'}
                                    />
                                    {publicForm.listingType === 'rent' && Number(publicForm.expectedRent || 0) > 0 && Number(publicForm.expectedRent || 0) < 5000 && (
                                        <div className="crm-muted" style={{ color: 'var(--crm-danger)', marginTop: '6px' }}>
                                            Can not be less than 5.00 K
                                        </div>
                                    )}
                                </div>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px', color: 'var(--crm-gray-900)' }}>
                                    <input
                                        type="checkbox"
                                        checked={Boolean(publicForm.amountNegotiable)}
                                        onChange={(e) => setPublicForm((prev) => ({ ...prev, amountNegotiable: e.target.checked }))}
                                    />
                                    Amount is Negotiable
                                </label>
                                {publicForm.listingType === 'rent' && (
                                    <div className="crm-form-group">
                                        <label className="crm-label required">Security Deposit</label>
                                        <input
                                            type="range"
                                            min={0}
                                            max={2000000}
                                            step={1000}
                                            value={Number(publicForm.securityDeposit || 0)}
                                            onChange={(e) => setPublicForm((prev) => ({ ...prev, securityDeposit: e.target.value }))}
                                            style={{ width: '100%', marginBottom: '10px' }}
                                        />
                                        <input
                                            type="number"
                                            className="crm-input"
                                            value={publicForm.securityDeposit}
                                            onChange={(e) => setPublicForm((prev) => ({ ...prev, securityDeposit: e.target.value }))}
                                            min={0}
                                            step={1000}
                                            placeholder="Security deposit in INR"
                                        />
                                    </div>
                                )}
                                <div className="crm-form-row">
                                    <div className="crm-form-group">
                                        <label className="crm-label required">City</label>
                                        <input
                                            type="text"
                                            className="crm-input"
                                            value={publicForm.city}
                                            onChange={(e) => setPublicForm((prev) => ({ ...prev, city: e.target.value }))}
                                        />
                                    </div>
                                    <div className="crm-form-group">
                                        <label className="crm-label required">Locality</label>
                                        <input
                                            type="text"
                                            className="crm-input"
                                            value={publicForm.locality}
                                            onChange={(e) => setPublicForm((prev) => ({ ...prev, locality: e.target.value }))}
                                        />
                                    </div>
                                    <div className="crm-form-group">
                                        <label className="crm-label required">Pincode</label>
                                        <input
                                            type="text"
                                            className="crm-input"
                                            value={publicForm.pincode}
                                            onChange={(e) => setPublicForm((prev) => ({ ...prev, pincode: e.target.value }))}
                                        />
                                    </div>
                                </div>
                                <div className="crm-form-group">
                                    <label className="crm-label required">Description</label>
                                    <textarea
                                        className="crm-textarea"
                                        rows={3}
                                        value={publicForm.description}
                                        onChange={(e) => setPublicForm((prev) => ({ ...prev, description: e.target.value }))}
                                    />
                                </div>
                                <label className="crm-label" style={{ color: 'var(--crm-gray-900)' }}>Property Images (Recommended For Quality Leads)</label>
                                <input
                                    type="file"
                                    accept="image/*"
                                    multiple
                                    onChange={handlePublicFilesChange}
                                    className="crm-input"
                                    style={{ marginBottom: '10px' }}
                                />
                                <textarea
                                    className="crm-textarea"
                                    rows={4}
                                    value={publicImages}
                                    onChange={(e) => setPublicImages(e.target.value)}
                                    placeholder="Paste image URLs, one per line"
                                />
                                <div className="crm-muted" style={{ marginTop: '6px' }}>
                                    Images are recommended for better quality leads.
                                </div>
                            </div>
                            {missingFields.length > 0 && (
                                <div style={{ marginTop: '16px', padding: '12px', border: '1px solid var(--crm-danger)', borderRadius: '10px', color: 'var(--crm-danger)' }}>
                                    <div style={{ fontWeight: 600, marginBottom: '6px' }}>Missing mandatory fields</div>
                                    <div style={{ fontSize: '13px' }}>{missingFields.join(', ')}</div>
                                </div>
                            )}
                        </div>
                        <div className="crm-modal-footer">
                            <button className="crm-btn crm-btn-secondary" onClick={handleClosePublicModal} disabled={isPushing || isSavingDraft}>
                                {isSavingDraft ? 'Saving...' : 'Cancel'}
                            </button>
                            <button
                                className="crm-btn crm-btn-primary"
                                onClick={handlePushToProphunt}
                                disabled={isPushing || isSavingDraft || !advertiseOnProphunt}
                            >
                                {isPushing ? 'Pushing...' : 'Push'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

export default PropertyCard;
